import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';
import { PROVINCES } from '../../context/RegionContext';
import { getCityLocation } from '../../utils/locationCatalog';
import { blogPosts, isPostPublished } from '../../data/BlogData';
import { productFaqs } from '../../data/productFaqs';
import SeoMeta, { SITE_URL, SITE_NAME } from './SeoMeta';

const DEFAULT_DESCRIPTION = 'Shop dressers, wardrobes and bedroom furniture available for delivery across Ontario from Sky Furniture Canada \u2014 fully assembled, cash on delivery, delivery in 3\u20135 business days.';

const titleCase = (value = '') => value
  .split('-')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

// Canonical route map: duplicate URL variants all point at one canonical form
// (the form used in sitemap.xml). Province hubs are /<provinceId> and city
// pages are /<provinceId>/<citySlug> for every province.
const canonicalizePath = (path, route) => {
  if (route.name === 'province') return `/${route.provinceId}`;
  if (route.name === 'city') {
    const resolvedProvinceId = getCityLocation(route.citySlug)?.provinceId || route.provinceId || 'ontario';
    return `/${resolvedProvinceId}/${route.citySlug}`;
  }
  if (route.name === 'product') return `/products/${route.productId}`;
  if (route.name === 'locations-alias') return '/locations';
  return path === '/' ? '/' : path;
};

const KNOWN_PROVINCE_IDS = new Set(PROVINCES.map((province) => province.id));

// Parse the current pathname into a structured route, independent of
// react-router's useParams (this component renders outside <Routes>).
const parseRoute = (rawPath) => {
  const path = rawPath.replace(/\/$/, '') || '/';
  const seg = path.split('/').filter(Boolean);

  if (path === '/') return { name: 'home', path };
  if (seg.length === 2 && (seg[0] === 'product' || seg[0] === 'products')) {
    return { name: 'product', path, productId: decodeURIComponent(seg[1]) };
  }
  if (seg[0] === 'shop' && seg[1] === 'city' && seg[2]) {
    return { name: 'city', path, citySlug: seg[2] };
  }
  if (seg[0] === 'shop' && seg[1] === 'province' && seg[2]) {
    return { name: 'province', path, provinceId: seg[2] };
  }
  if (seg.length === 1 && KNOWN_PROVINCE_IDS.has(seg[0])) {
    return { name: 'province', path, provinceId: seg[0] };
  }
  if (seg.length === 2 && KNOWN_PROVINCE_IDS.has(seg[0])) {
    return { name: 'city', path, provinceId: seg[0], citySlug: seg[1] };
  }
  if (seg[0] === 'province' && seg[1]) {
    return { name: 'province', path, provinceId: seg[1] };
  }
  if (seg[0] === 'categories' && seg[1]) {
    return { name: 'category', path, categorySlug: seg[1], subcategorySlug: seg[2] || '' };
  }
  if (seg[0] === 'blog' && seg[1]) {
    return { name: 'blog-post', path, slug: seg[1] };
  }
  const staticRoutes = new Set([
    'products', 'shop', 'contact', 'checkout', 'locations', 'delivery-areas',
    'shipping', 'returns', 'faq', 'warranty', 'about', 'blog', 'cart',
  ]);
  if (seg.length === 1 && staticRoutes.has(seg[0])) {
    return { name: seg[0] === 'delivery-areas' ? 'locations-alias' : 'static', path, key: seg[0] };
  }
  if (seg[0] === 'order-confirmation' && seg[1]) return { name: 'order', path };
  if (seg[0] === 'admin') return { name: 'admin', path };
  return { name: 'not-found', path };
};

const getImage = (product) => product?.images?.[0] || product?.image;

const SEO = () => {
  const location = useLocation();
  const { allProducts } = useProducts();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const route = useMemo(() => parseRoute(location.pathname), [location.pathname]);
  const path = route.path;

  const isSearchOrFilter = Boolean(query.get('search') || query.get('category') || query.get('subcategory'));
  const product = route.productId ? allProducts.find((item) => String(item.id) === String(route.productId)) : null;
  const foundPost = route.name === 'blog-post' ? blogPosts.find((item) => item.slug === route.slug) : null;
  // Keep future-dated posts (weekly drip) out of Article schema until published.
  const blogPost = foundPost && isPostPublished(foundPost) ? foundPost : null;
  const cityLocation = route.citySlug ? getCityLocation(route.citySlug) : null;
  const resolvedProvinceId = cityLocation?.provinceId || route.provinceId || '';
  const province = PROVINCES.find((item) => item.id === resolvedProvinceId);
  const categoryName = route.categorySlug ? titleCase(route.categorySlug) : query.get('category') ? titleCase(query.get('category')) : '';
  const cityName = cityLocation?.city || '';

  const page = useMemo(() => {
    if (route.name === 'not-found') {
      return {
        title: `Page Not Found | ${SITE_NAME}`,
        description: 'The page you are looking for does not exist. Browse Sky Furniture Canada for furniture available for Ontario delivery.',
        type: 'website', noindex: true,
      };
    }
    if (route.name === 'product' && product) {
      const locationLabel = province?.name || 'Ontario';
      return {
        title: `${product.name} | ${locationLabel} Furniture | ${SITE_NAME}`,
        description: `${product.description || product.name} Available for delivery in ${locationLabel}. Shop Sky Furniture Canada.`,
        type: 'product', image: getImage(product),
      };
    }
    if (route.name === 'product' && !product) {
      return {
        title: `Product Not Found | ${SITE_NAME}`,
        description: 'This product is no longer available. Browse Sky Furniture Canada for furniture available for Ontario delivery.',
        type: 'website', noindex: true,
      };
    }
    if (path === '/') return { title: `${SITE_NAME} | Ontario Furniture Delivery`, description: DEFAULT_DESCRIPTION, type: 'website' };
    if (path === '/shop' || path === '/products') return { title: `Shop Furniture in Ontario | ${SITE_NAME}`, description: 'Browse Sky Furniture Canada products by category, price, colour and availability for Ontario delivery.', type: 'collection' };
    if (route.name === 'category' || categoryName) return { title: `${categoryName} in Ontario | ${SITE_NAME}`, description: `Shop ${categoryName.toLowerCase()} available for Ontario delivery from Sky Furniture Canada. Browse current products, prices and availability.`, type: 'collection' };
    if (cityName) return { title: `Furniture Delivery in ${cityName}, ${province?.name || 'Ontario'} | ${SITE_NAME}`, description: `Browse furniture currently listed for delivery in ${cityName}, ${province?.name || 'Ontario'}. Shop dressers, wardrobes and storage furniture from Sky Furniture Canada.`, type: 'location' };
    if (province) return { title: `Furniture Delivery in ${province.name} | ${SITE_NAME}`, description: `Shop furniture currently available for delivery in ${province.name}. Browse local products, categories and cities from Sky Furniture Canada.`, type: 'location' };
    if (route.name === 'blog-post' && blogPost) return { title: `${blogPost.title} | ${SITE_NAME}`, description: blogPost.excerpt || DEFAULT_DESCRIPTION, type: 'article', image: blogPost.image };
    const staticPages = {
      '/faq': ['Furniture FAQs | Sky Furniture Canada', 'Answers about furniture sizes, colours, delivery, pickup, availability and returns from Sky Furniture Canada.'],
      '/shipping': ['Furniture Delivery Information | Sky Furniture Canada', 'Review Sky Furniture Canada delivery areas, delivery charges, pickup options and preferred delivery information.'],
      '/returns': ['Furniture Returns and Exchanges | Sky Furniture Canada', 'Read the Sky Furniture Canada returns and exchanges policy before placing your order.'],
      '/contact': ['Contact Sky Furniture Canada', 'Contact Sky Furniture Canada about furniture availability, custom requests, delivery and product questions.'],
      '/about': ['About Sky Furniture Canada', 'Learn about Sky Furniture Canada and our furniture catalogue for practical Canadian homes.'],
      '/blog': ['Furniture Buying Guides | Sky Furniture Canada', 'Practical furniture buying guides covering bedroom sets, mattresses, storage, delivery and room planning.'],
      '/locations': ['Furniture Service Areas | Sky Furniture Canada', 'Explore Sky Furniture Canada furniture delivery and pickup service areas across Ontario and Canada.'],
    };
    const [title, description] = staticPages[path] || [`${SITE_NAME} | Furniture for Canadian Homes`, DEFAULT_DESCRIPTION];
    return { title, description, type: 'website' };
  }, [path, route, product, blogPost, province, categoryName, cityName]);

  const { canonical, noindex, jsonLd } = useMemo(() => {
    const canonicalPath = canonicalizePath(path, route);
    const canonical = `${SITE_URL}${canonicalPath === '/' ? '/' : canonicalPath}`;
    const noindex = Boolean(
      page.noindex || route.name === 'admin' || path === '/checkout'
      || path.startsWith('/order-confirmation') || path === '/cart'
      || (isSearchOrFilter && !path.startsWith('/categories/')),
    );

    const organization = {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/images/logo/skyf-monogram.png`,
      areaServed: { '@type': 'AdministrativeArea', name: 'Ontario, Canada' },
      sameAs: [],
    };
    const website = { '@type': 'WebSite', '@id': `${SITE_URL}/#website`, name: SITE_NAME, url: `${SITE_URL}/`, publisher: { '@id': `${SITE_URL}/#organization` } };
    const graph = [organization, website];

    const breadcrumbItems = [{ name: 'Home', url: `${SITE_URL}/` }];
    if (province) breadcrumbItems.push({ name: province.name, url: `${SITE_URL}/${province.id}` });
    if (cityName) breadcrumbItems.push({ name: cityName, url: `${SITE_URL}/${resolvedProvinceId}/${route.citySlug}` });
    if (categoryName) breadcrumbItems.push({ name: categoryName, url: `${SITE_URL}/categories/${route.categorySlug || query.get('category')}` });
    if (product) breadcrumbItems.push({ name: product.name, url: `${SITE_URL}/products/${product.id}` });
    if (breadcrumbItems.length > 1) graph.push({ '@type': 'BreadcrumbList', itemListElement: breadcrumbItems.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: item.url })) });

    if (product) {
      const price = product.provincePrices?.[province?.id || 'ontario'] ?? product.priceCAD;
      graph.push({
        '@type': 'Product',
        '@id': `${SITE_URL}/products/${product.id}#product`,
        name: product.name,
        description: product.description,
        image: (product.images || [product.image]).filter(Boolean).map((image) => image.startsWith('http') ? image : `${SITE_URL}${image}`),
        sku: String(product.id),
        category: product.category,
        brand: { '@type': 'Brand', name: SITE_NAME },
        offers: { '@type': 'Offer', url: `${SITE_URL}/products/${product.id}`, priceCurrency: 'CAD', price: price ?? 0, availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock', seller: { '@id': `${SITE_URL}/#organization` } },
      });
      graph.push({ '@type': 'FAQPage', mainEntity: productFaqs.map((faq) => ({ '@type': 'Question', name: faq.q, acceptedAnswer: { '@type': 'Answer', text: faq.a } })) });
    } else if (path === '/faq') {
      const faqQuestions = [
        ['How do I place an order?', 'Browse the collection, add items to your cart, complete the delivery information and submit your order.'],
        ['Do you deliver to my area?', 'Sky Furniture Canada lists products by province and city. Open the relevant location page or check availability during checkout.'],
        ['Can I choose a delivery date?', 'You can provide a preferred delivery date during checkout and our team will confirm the delivery details.'],
        ['What is your return policy?', 'Eligible items may be returned within the published return period when they meet the condition requirements described on the Returns page.'],
        ['Are items shown in stock?', 'Products displayed as in stock are currently listed in the catalogue. Availability is also checked against the selected province and city.'],
        ['What warranty do you offer?', 'Warranty coverage and exclusions are described on the Warranty Information page and may vary by product.'],
      ];
      graph.push({ '@type': 'FAQPage', mainEntity: faqQuestions.map(([question, answer]) => ({ '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer } })) });
    } else if (page.type === 'collection' || page.type === 'location') {
      const visibleProducts = allProducts.filter((item) => item.inStock !== false).slice(0, 24);
      graph.push({ '@type': 'CollectionPage', name: page.title, url: canonical, mainEntity: { '@type': 'ItemList', numberOfItems: visibleProducts.length, itemListElement: visibleProducts.map((item, index) => ({ '@type': 'ListItem', position: index + 1, url: `${SITE_URL}/products/${item.id}`, name: item.name })) } });
    } else if (blogPost) {
      const authorIsOrg = /sky furniture/i.test(blogPost.author || '');
      graph.push({
        '@type': 'Article',
        '@id': `${SITE_URL}/blog/${blogPost.slug}#article`,
        headline: blogPost.title,
        description: blogPost.excerpt,
        image: blogPost.image ? [blogPost.image.startsWith('http') ? blogPost.image : `${SITE_URL}${blogPost.image}`] : [],
        datePublished: blogPost.date,
        author: authorIsOrg
          ? { '@type': 'Organization', name: blogPost.author, url: `${SITE_URL}/` }
          : { '@type': 'Person', name: blogPost.author },
        publisher: { '@id': `${SITE_URL}/#organization` },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      });
    }
    return { canonical, noindex, jsonLd: { '@context': 'https://schema.org', '@graph': graph } };
  }, [path, route, page, product, blogPost, province, cityName, categoryName, isSearchOrFilter, query, allProducts]);

  return (
    <SeoMeta
      title={page.title}
      description={page.description}
      canonical={canonical}
      noindex={noindex}
      ogType={page.type === 'product' ? 'product' : page.type === 'article' ? 'article' : 'website'}
      image={page.image || ''}
      jsonLd={jsonLd}
    />
  );
};

export default SEO;
